import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {IconButtonNew, LinkButton} from '@components/common/base/buttons'
import {Checkbox} from '@components/common/base/checkbox'
import {Dialog} from '@components/common/base/dialog/dialog'
import {TextArea} from '@components/textarea'
import {cn} from '@containers/html-utils'
import {useChatStore} from '../chat/chat-store'
import {useRightPanelStore} from './right-panel-store'
import {votingStore} from './voting-store'

const TwTextArea = tw(TextArea)`
  !outline-none
  !shadow-none
  !bg-transparent
  !w-full
  m-0

  text-primary
  placeholder:text-placeholder

  border-solid
  border-1
  border-base

  focus:border-interactive-l80
  rounded-8

  text-sm-regular

  pl-12
  pr-36
  py-8
  max-h-112
`

const TwIcon = tw(IconButtonNew)`
  absolute
  right-12
  top-8
  cursor-pointer
`

export const VotingPopup = observer(() => {
  const [state] = useRightPanelStore()

  if (!state.showPopupVoting) {
    return null
  }

  return <VotingPopupBase />
})

const VotingPopupBase = observer(() => {
  const submitDisabled = !votingStore.canCreateVoting()
  const [, chatStore] = useChatStore()

  return (
    <Dialog
      close={votingStore.close}
      cancel={votingStore.cancel}
      submit={() => votingStore.submit(chatStore.state.hub)}
      title={Lang.msg.voting.popupTitle}
      width={400}
      primaryBtnText={Lang.msg.voting.createVoting}
      skipSubmitBySpace
      primaryBtnDisabled={submitDisabled}
      popupInWnd
      noScroll={false}
    >
      <div className="flex flex-col gap-20 mt-8">
        <div className="flex flex-col gap-8">
          <div className="text-secondary text-sm-regular cursor-default">
            {Lang.msg.voting.popupCaption}
          </div>

          <TwTextArea
            placeholder={Lang.msg.voting.questionPlaceholder}
            autoFocus
            onChange={ev => votingStore.setQuestion(ev.target?.value || '')}
            value={votingStore.state.question || ''}
          />
        </div>

        <div className="flex flex-col gap-8 voting-content">
          <div className="text-secondary text-sm-regular cursor-default">
            {Lang.msg.voting.popupOptions}
          </div>

          {votingStore.state.answers.map((answer, id) => {
            return (
              <div key={id} className={'relative'}>
                <TwTextArea
                  placeholder={Lang.msg.voting.answerPlaceholder + (id + 1)}
                  onChange={ev =>
                    votingStore.setAnswer(id, ev.target?.value || '')
                  }
                  value={answer.text || ''}
                />

                <TwIcon
                  icon="IconCustomClose"
                  onClick={() => {
                    votingStore.removeAnswer(id)
                  }}
                />
              </div>
            )
          })}

          {!votingStore.maxAnswers() && (
            <LinkButton
              icon="IconCustomPlus"
              iconPadding="4"
              noPaddings
              onClick={votingStore.addAnswer}
            >
              {Lang.msg.voting.addAnswer}
            </LinkButton>
          )}

          <div
            className={cn(
              'text-xs-regular cursor-default',
              votingStore.invalidCountAnswers
                ? 'text-error'
                : 'text-placeholder'
            )}
          >
            {getHintText()}
          </div>
        </div>

        <Checkbox
          text={Lang.msg.voting.addFewVar}
          value={votingStore.state.multiSelectOn}
          onChange={val => {
            votingStore.setMultiSelectOn(val)
          }}
        />
      </div>
    </Dialog>
  )
})

export const getHintText = (): string => {
  if (votingStore.invalidCountAnswers) {
    return Lang.msg.voting.errorCountAnswers
  }

  if (votingStore.maxAnswers()) {
    return Lang.msg.voting.addAnswerHintMax
  }

  return Lang.msg.voting.addAnswerHint(votingStore.leftCountAnswers)
}
